import { useCallback, useState } from 'react';
import { RefreshCw, RotateCw, Search } from 'lucide-react';
import {
  CanalEnvio,
  MAXIMO_REENVIOS,
  ProvedorEnvio,
  formatarDataHora,
  type EmpresaDoEnvioDto,
  type EnvioComFalhaDto,
  type FiltroEnvios,
  type FiltroReenvio,
  type ResultadoReenvioDto,
  type ResumoFalhaDto,
} from '@nexital/shared';
import { useDependencias, useEnvio } from '../../application/dependencias';
import { useConfirmar } from '../design-system/Confirmacao';
import { CANAIS, DetalheEnvio, PROVEDORES, SITUACOES, SeloFalha, TIPOS_FALHA } from './DetalheEnvio';
import { useCarga, useGravar } from './campos';

const REENVIOS: Record<FiltroReenvio, string> = { 'nao-reenviadas': 'Ainda não reenviadas', reenviadas: 'Já reenviadas', reenvios: 'Só os reenvios' };
const PROVEDORES_DO_FILTRO = [ProvedorEnvio.Mailgun, ProvedorEnvio.AmazonSes, ProvedorEnvio.Telegram];

/** Texto do resultado de um reenvio, com o primeiro motivo das que ficaram de fora. */
export function textoDoReenvio(r: ResultadoReenvioDto) {
  const feitas = r.reenviadas === 1 ? '1 mensagem reenviada' : `${r.reenviadas} mensagens reenviadas`;
  if (r.ignoradas.length === 0) return `${feitas}. Elas entram na fila e saem aos poucos.`;
  return `${feitas}; ${r.ignoradas.length} não: ${r.ignoradas[0]!.motivo}`;
}

function Resumo({ resumo, tipo, filtrar, reenviarTodas }: { resumo: ResumoFalhaDto[]; tipo?: number; filtrar: (t?: number) => void; reenviarTodas: (r: ResumoFalhaDto) => void }) {
  if (resumo.length === 0) return null;
  return (
    <div className="config__envio-resumo" role="group" aria-label="Falhas por tipo">
      {resumo.map((r) => (
        <div key={r.tipoFalha} className={`config__envio-tipo${tipo === r.tipoFalha ? ' config__envio-tipo--ativo' : ''}`}>
          <button type="button" className="config__envio-tipo-filtro" aria-pressed={tipo === r.tipoFalha} onClick={() => filtrar(tipo === r.tipoFalha ? undefined : r.tipoFalha)}>
            <strong>{r.total}</strong>
            <span>{TIPOS_FALHA[r.tipoFalha]}</span>
            <small>Última em {formatarDataHora(r.ultimaEm)}</small>
          </button>
          {r.podemReenviar > 0 && (
            <button type="button" className="botao botao--secundario" onClick={() => reenviarTodas(r)}>
              <RotateCw size={14} aria-hidden /> Reenviar todas ({r.podemReenviar})
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

type PropsFiltros = { filtro: FiltroEnvios; mudar: (f: Partial<FiltroEnvios>) => void; empresas: EmpresaDoEnvioDto[] | null };

function Opcoes({ rotulo, valor, opcoes, mudar }: { rotulo: string; valor: string; opcoes: [string, string][]; mudar: (v: string) => void }) {
  return (
    <label className="campo config__campo config__campo--curta">
      <span>{rotulo}</span>
      <select value={valor} onChange={(e) => mudar(e.target.value)}>
        <option value="">Todos</option>
        {opcoes.map(([v, r]) => (
          <option key={v} value={v}>
            {r}
          </option>
        ))}
      </select>
    </label>
  );
}

const numero = (v: string) => (v ? Number(v) : undefined);

function Filtros({ filtro, mudar, empresas }: PropsFiltros) {
  const [busca, definirBusca] = useState(filtro.destinatario ?? '');
  return (
    <div className="config__envio-filtros">
      <Opcoes rotulo="Canal" valor={String(filtro.canal ?? '')} opcoes={[CanalEnvio.Email, CanalEnvio.Chat].map((c) => [String(c), CANAIS[c]!])} mudar={(v) => mudar({ canal: numero(v) })} />
      <Opcoes rotulo="Provedor" valor={String(filtro.provedor ?? '')} opcoes={PROVEDORES_DO_FILTRO.map((p) => [String(p), PROVEDORES[p]!])} mudar={(v) => mudar({ provedor: numero(v) })} />
      <Opcoes
        rotulo="Adianta reenviar?"
        valor={filtro.reenviavel === undefined ? '' : String(filtro.reenviavel)}
        opcoes={[
          ['true', 'Sim'],
          ['false', 'Não'],
        ]}
        mudar={(v) => mudar({ reenviavel: v ? v === 'true' : undefined })}
      />
      <Opcoes rotulo="Reenvio" valor={filtro.reenvio ?? ''} opcoes={Object.entries(REENVIOS)} mudar={(v) => mudar({ reenvio: (v || undefined) as FiltroReenvio | undefined })} />
      <label className="campo config__campo config__campo--curta">
        <span>De</span>
        <input type="date" value={filtro.de ?? ''} onChange={(e) => mudar({ de: e.target.value || undefined })} />
      </label>
      <label className="campo config__campo config__campo--curta">
        <span>Até</span>
        <input type="date" value={filtro.ate ?? ''} onChange={(e) => mudar({ ate: e.target.value || undefined })} />
      </label>
      <form
        className="config__busca"
        onSubmit={(e) => {
          e.preventDefault();
          mudar({ destinatario: busca.trim() || undefined });
        }}
      >
        <Search size={16} aria-hidden />
        <input value={busca} placeholder="Destinatário (Enter para buscar)" aria-label="Buscar por destinatário" onChange={(e) => definirBusca(e.target.value)} />
      </form>
      {empresas && <Empresas filtro={filtro} mudar={mudar} empresas={empresas} />}
    </div>
  );
}

/** Só para o superadministrador: várias empresas e as mensagens sem empresa (do sistema). */
function Empresas({ filtro, mudar, empresas }: PropsFiltros & { empresas: EmpresaDoEnvioDto[] }) {
  const escolhidas = filtro.empresas ?? [];
  const alternar = (uuid: string) => mudar({ empresas: escolhidas.includes(uuid) ? escolhidas.filter((u) => u !== uuid) : [...escolhidas, uuid] });
  const rotulo = escolhidas.length || filtro.semEmpresa ? `${escolhidas.length} empresa(s)${filtro.semEmpresa ? ' + sem empresa' : ''}` : 'Esta empresa';
  return (
    <details className="config__envio-empresas">
      <summary>Empresas: {rotulo}</summary>
      <label className="config__marcar">
        <input type="checkbox" checked={filtro.semEmpresa ?? false} onChange={(e) => mudar({ semEmpresa: e.target.checked || undefined })} /> Sem empresa (do sistema)
      </label>
      {empresas.map((e) => (
        <label key={e.uuid} className="config__marcar">
          <input type="checkbox" checked={escolhidas.includes(e.uuid)} onChange={() => alternar(e.uuid)} /> {e.nome}
        </label>
      ))}
    </details>
  );
}

function Reenvios({ e }: { e: EnvioComFalhaDto }) {
  if (e.numeroReenvio > 0) return <span className="pilula pilula--lilas">Reenvio {e.numeroReenvio}</span>;
  if (e.reenvios === 0) return <small>—</small>;
  return (
    <span>
      {e.reenvios} de {MAXIMO_REENVIOS} reenvios
      {e.ultimoReenvio && <small>Último: {SITUACOES[e.ultimoReenvio.situacao]}</small>}
    </span>
  );
}

function Linha({ e, abrir, reenviar, mostrarEmpresa }: { e: EnvioComFalhaDto; abrir: () => void; reenviar: () => void; mostrarEmpresa: boolean }) {
  return (
    <tr className="config__envio-linha" onClick={abrir}>
      <td>
        {formatarDataHora(e.falhouEm ?? e.criadoEm)}
        <small>
          {CANAIS[e.canal]}
          {e.provedor && ` · ${PROVEDORES[e.provedor]}`}
        </small>
      </td>
      <td>
        <strong>{e.destinatarios.join(', ') || '—'}</strong>
        <small>
          {mostrarEmpresa && `${e.empresa?.nome ?? 'Sem empresa'} · `}
          {e.assunto}
        </small>
      </td>
      <td>
        <SeloFalha e={e} />
        <small className="config__envio-motivo">{e.motivo}</small>
      </td>
      <td>
        {e.tentativas}/{e.maxTentativas}
      </td>
      <td>
        <Reenvios e={e} />
      </td>
      <td onClick={(ev) => ev.stopPropagation()}>
        {e.podeReenviar && (
          <div className="config__acoes-linha">
            <button type="button" className="botao-icone" aria-label="Reenviar" title="Reenviar" onClick={reenviar}>
              <RotateCw size={16} />
            </button>
          </div>
        )}
      </td>
    </tr>
  );
}

/** Os filtros valem na hora; "Atualizar" relê a lista sem mudar os filtros (falhas novas do worker ou do webhook). */
function Barra({ semPeriodo, atualizar }: { semPeriodo: boolean; atualizar: () => void }) {
  return (
    <div className="config__envio-barra">
      <p className="config__nota">
        Os filtros valem na hora (o destinatário, com Enter).{semPeriodo && ' Sem período escolhido, a lista mostra os últimos 30 dias.'}
      </p>
      <button type="button" className="botao botao--secundario" onClick={atualizar}>
        <RefreshCw size={14} aria-hidden /> Atualizar
      </button>
    </div>
  );
}

function Paginas({ pagina, total, porPagina, ir }: { pagina: number; total: number; porPagina: number; ir: (p: number) => void }) {
  const ultima = Math.max(1, Math.ceil(total / porPagina));
  if (ultima === 1) return null;
  return (
    <div className="config__rodape">
      <button type="button" className="botao botao--secundario" disabled={pagina <= 1} onClick={() => ir(pagina - 1)}>
        Anterior
      </button>
      <span>
        Página {pagina} de {ultima}
      </span>
      <button type="button" className="botao botao--secundario" disabled={pagina >= ultima} onClick={() => ir(pagina + 1)}>
        Próxima
      </button>
    </div>
  );
}

/**
 * Envios com falha (RQ-MSG-009 a 011): resumo por tipo de falha no alto, filtros, lista e detalhe. O reenvio cria uma
 * mensagem nova a partir da original (até 3) e só para quem falhou; convite não é reenviado (quem convidou é avisado).
 */
export function SecaoEnvios() {
  const { configuracoes } = useDependencias();
  const avisar = useEnvio((s) => s.avisar);
  const confirmar = useConfirmar();
  const { gravar } = useGravar();
  const [filtro, definirFiltro] = useState<FiltroEnvios>({});
  const [aberto, definirAberto] = useState<string | null>(null);
  const carregar = useCallback(() => configuracoes.envios(filtro), [configuracoes, filtro]);
  const { dados, erro, recarregar } = useCarga(carregar);
  const mudar = (f: Partial<FiltroEnvios>) => definirFiltro((atual) => ({ ...atual, ...f, pagina: undefined }));

  const reenviar = async (entrada: Parameters<typeof configuracoes.reenviar>[0], titulo: string, mensagem: string) => {
    if (!(await confirmar({ titulo, mensagem, rotulo: 'Reenviar' }))) return;
    let resultado: ResultadoReenvioDto | undefined;
    await gravar(async () => (resultado = await configuracoes.reenviar(entrada)), 'Reenvio pedido.');
    if (resultado) avisar(textoDoReenvio(resultado));
    void recarregar();
  };
  const reenviarUma = (e: EnvioComFalhaDto) =>
    reenviar({ uuids: [e.uuid] }, 'Reenviar a mensagem?', `Sai uma mensagem nova, só para quem não recebeu (${e.reenvios + 1}º de ${MAXIMO_REENVIOS} reenvios).`);
  const reenviarTodas = (r: ResumoFalhaDto) =>
    reenviar(
      { filtro: { ...filtro, tipoFalha: r.tipoFalha, pagina: undefined } },
      `Reenviar ${r.podemReenviar} mensagens?`,
      `Todas as de "${TIPOS_FALHA[r.tipoFalha]}" que podem ser reenviadas, dentro dos filtros. Elas saem aos poucos, num lote.`,
    );

  if (erro) return <p className="cartao-acesso__erro">{erro}</p>;
  if (!dados) return <p>Carregando…</p>;
  return (
    <div className="config__formulario">
      <Resumo resumo={dados.resumo} tipo={filtro.tipoFalha} filtrar={(t) => mudar({ tipoFalha: t })} reenviarTodas={(r) => void reenviarTodas(r)} />
      <Filtros filtro={filtro} mudar={mudar} empresas={dados.empresas} />
      <Barra semPeriodo={!filtro.de && !filtro.ate} atualizar={() => void recarregar()} />
      {dados.envios.length === 0 ? (
        <p className="config__vazio">Nenhum envio com falha neste filtro.</p>
      ) : (
        <table className="config__tabela">
          <thead>
            <tr>
              <th>Quando</th>
              <th>Para</th>
              <th>Falha</th>
              <th>Tentativas</th>
              <th>Reenvios</th>
              <th aria-label="Ações" />
            </tr>
          </thead>
          <tbody>
            {dados.envios.map((e) => (
              <Linha key={e.uuid} e={e} mostrarEmpresa={dados.empresas !== null} abrir={() => definirAberto(e.uuid)} reenviar={() => void reenviarUma(e)} />
            ))}
          </tbody>
        </table>
      )}
      <Paginas pagina={dados.pagina} total={dados.total} porPagina={dados.porPagina} ir={(p) => definirFiltro((atual) => ({ ...atual, pagina: p }))} />
      {aberto && <DetalheEnvio uuid={aberto} abrir={definirAberto} fechar={() => definirAberto(null)} reenviar={(e) => void reenviarUma(e)} />}
    </div>
  );
}
